/**
 * Mission Type Definitions
 *
 * Defines data structures for complete missions in KOPASGAT Drone Planner.
 */

import type { Waypoint } from './waypoint.types';
import type { MissionSettings, MissionValidation } from './settings.types';

/**
 * Mission status states
 */
export enum MissionStatus {
  PLANNING = 'planning',         // Being created/edited
  READY = 'ready',               // Validated and ready to upload
  UPLOADING = 'uploading',       // Currently uploading to vehicle
  UPLOADED = 'uploaded',         // Successfully uploaded to vehicle
  ACTIVE = 'active',             // Currently executing on vehicle
  PAUSED = 'paused',             // Execution paused
  COMPLETED = 'completed',       // Successfully completed
  FAILED = 'failed',             // Upload or execution failed
  ERROR = 'error',               // Error state
}

/**
 * Vehicle types supported by the system
 */
export enum VehicleType {
  COPTER = 'copter',             // Multicopter (quadcopter, hexacopter, etc.)
  PLANE = 'plane',               // Fixed-wing aircraft
  ROVER = 'rover',               // Ground vehicle
  SUB = 'sub',                   // Submarine/underwater vehicle
  VTOL = 'vtol',                 // Vertical Take-Off and Landing aircraft
}

/**
 * Geographic position
 */
export interface Position {
  latitude: number;              // Decimal degrees
  longitude: number;             // Decimal degrees
  altitude: number;              // Meters
}

/**
 * Complete mission definition
 */
export interface Mission {
  // Identity
  id: string;                    // Unique identifier (UUID)
  name: string;                  // Mission name (e.g., "Mission_20250104_120000")
  description?: string;          // Optional mission description

  // Waypoints
  waypoints: Waypoint[];         // Ordered list of waypoints (sequence 0, 1, 2, ...)
  homePosition?: Position;       // Home/launch position (optional - auto-set by vehicle)

  // Mission Parameters
  defaultAltitude: number;       // Default altitude for new waypoints (meters)
  defaultSpeed: number;          // Default speed (m/s)
  cruiseSpeed: number;           // Cruise speed in flight (m/s)
  maxSpeed: number;              // Maximum allowed speed (m/s)

  // Vehicle Configuration
  vehicleType: VehicleType;      // Type of vehicle for this mission

  // Mission Settings (grouped configuration)
  settings?: MissionSettings;    // Complete mission settings (optional for backward compatibility)

  // Validation
  validation?: MissionValidation; // Mission validation state (optional)

  // Status
  status: MissionStatus;         // Current mission status

  // Statistics (calculated in real-time)
  totalDistance: number;         // Total mission distance (meters)
  estimatedTime: number;         // Estimated flight time (seconds)
  estimatedBattery: number;      // Estimated battery % required
  maxAltitude: number;           // Maximum altitude in mission (meters)
  minAltitude: number;           // Minimum altitude in mission (meters)
  altitudeChange: number;        // Total altitude change (meters)

  // Metadata
  createdAt: Date;               // When mission was created
  updatedAt: Date;               // Last modification time
  lastUploadedAt?: Date;         // When mission was last uploaded to vehicle
  version: number;               // Mission version number (increments on save)
}

/**
 * Mission statistics (real-time calculated values)
 */
export interface MissionStatistics {
  waypointCount: number;         // Number of waypoints
  totalDistance: number;         // Total horizontal distance (meters)
  horizontalDistance: number;    // 2D distance (without altitude)
  verticalDistance: number;      // Total altitude changes (meters)
  estimatedFlightTime: number;   // Estimated time (seconds)
  estimatedBatteryUsage: number; // Estimated battery % (0-100)
  maxAltitude: number;           // Highest waypoint altitude (meters)
  minAltitude: number;           // Lowest waypoint altitude (meters)
  averageAltitude: number;       // Average altitude (meters)
  averageSpeed: number;          // Average cruise speed (m/s)
}

/**
 * Mission creation input
 */
export interface MissionInput {
  name: string;
  description?: string;
  defaultAltitude?: number;
  defaultSpeed?: number;
  vehicleType?: VehicleType;
}

/**
 * Mission bounds (bounding box for map view)
 */
export interface MissionBounds {
  north: number;                 // Maximum latitude
  south: number;                 // Minimum latitude
  east: number;                  // Maximum longitude
  west: number;                  // Minimum longitude
}

/**
 * Mission export formats
 */
export enum ExportFormat {
  JSON = 'json',                 // Native JSON format
  QGC = 'qgc',                   // QGroundControl .plan format
  KML = 'kml',                   // KML for Google Earth
  CSV = 'csv',                   // CSV for analysis
}

/**
 * Type guard for Mission
 */
export function isMission(value: unknown): value is Mission {
  if (!value || typeof value !== 'object') return false;

  const mission = value as Mission;
  return (
    typeof mission.id === 'string' &&
    typeof mission.name === 'string' &&
    Array.isArray(mission.waypoints) &&
    typeof mission.status === 'string'
  );
}
